import { MdPerson, MdEdit, MdDelete, MdVisibility } from 'react-icons/md'
import { Button } from '../ui'

function PilotCard({ pilot, onView, onEdit, onDelete }) {
  // const getStatusIcon = (status) => {
  //   switch (status) {
  //     case 'active':
  //       return <MdWork className="text-success-600 dark:text-success-400" />
  //     case 'available':
  //       return <MdSchedule className="text-blue-600 dark:text-blue-400" />
  //     case 'on-leave':
  //       return <MdWarning className="text-warning-600 dark:text-warning-400" />
  //     default:
  //       return <MdPerson className="text-neutral-500 dark:text-neutral-400" />
  //   }
  // }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      case 'available':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
      case 'on-leave':
        return 'bg-warning-100 dark:bg-warning-900 text-warning-600 dark:text-warning-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  const getDaysUntilExpiry = (dateString) => {
    if (!dateString) return null
    const expiryDate = new Date(dateString)
    const today = new Date()
    const diffTime = expiryDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getExpiryStatus = (dateString) => {
    const daysUntil = getDaysUntilExpiry(dateString)
    if (daysUntil === null) return 'unknown'
    if (daysUntil < 0) return 'expired'
    if (daysUntil <= 30) return 'warning'
    return 'good'
  }

  const getExpiryColor = (status) => {
    switch (status) {
      case 'expired':
        return 'text-red-600 dark:text-red-400'
      case 'warning':
        return 'text-warning-600 dark:text-warning-400'
      case 'good':
        return 'text-success-600 dark:text-success-400'
      default:
        return 'text-neutral-500 dark:text-neutral-400'
    }
  }

  const licenseExpiryStatus = getExpiryStatus(pilot.licenseExpiry)
  const medicalExpiryStatus = getExpiryStatus(pilot.dateOfNextMedical)

  return (
    <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
            <MdPerson className="text-primary-600 dark:text-primary-400 text-xl" />
          </div>
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-white">
              {pilot.firstName} {pilot.lastName}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">{pilot.number}</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <Button variant="ghost" size="sm" onClick={onView} className="p-1" title="View Details">
            <MdVisibility className="text-lg" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} className="p-1" title="Edit Pilot">
            <MdEdit className="text-lg" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Delete Pilot"
          >
            <MdDelete className="text-lg" />
          </Button>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          {/* {getStatusIcon(pilot.status)} */}
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(pilot.status)}`}
          >
            {pilot.status?.charAt(0).toUpperCase() + pilot.status?.slice(1)}
          </span>
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">{pilot.email}</p>
      </div>

      {/* Key Information */}
      <div className="space-y-3 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Date of Hire:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {formatDate(pilot.dateOfHire)}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Total Flight Hours:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {pilot.totalFlightHours?.toLocaleString() || 'N/A'}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">License:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {pilot.licenseNumber || 'N/A'}
          </span>
        </div>
      </div>

      {/* Expiry Warnings */}
      <div className="space-y-2">
        {pilot.licenseExpiry && (
          <div className="flex justify-between items-center text-xs">
            <span className="text-neutral-600 dark:text-neutral-400">License Expires:</span>
            <span className={`font-medium ${getExpiryColor(licenseExpiryStatus)}`}>
              {formatDate(pilot.licenseExpiry)}
              {licenseExpiryStatus === 'expired' && ' (Expired)'}
              {licenseExpiryStatus === 'warning' &&
                ` (${getDaysUntilExpiry(pilot.licenseExpiry)} days)`}
            </span>
          </div>
        )}

        {pilot.dateOfNextMedical && (
          <div className="flex justify-between items-center text-xs">
            <span className="text-neutral-600 dark:text-neutral-400">Medical Expires:</span>
            <span className={`font-medium ${getExpiryColor(medicalExpiryStatus)}`}>
              {formatDate(pilot.dateOfNextMedical)}
              {medicalExpiryStatus === 'expired' && ' (Expired)'}
              {medicalExpiryStatus === 'warning' &&
                ` (${getDaysUntilExpiry(pilot.dateOfNextMedical)} days)`}
            </span>
          </div>
        )}
      </div>

      {/* Type Ratings */}
      {pilot.typeRatings && pilot.typeRatings.length > 0 && (
        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-2">Type Ratings:</p>
          <div className="flex flex-wrap gap-1">
            {pilot.typeRatings.slice(0, 3).map((rating, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded"
              >
                {rating}
              </span>
            ))}
            {pilot.typeRatings.length > 3 && (
              <span className="px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded">
                +{pilot.typeRatings.length - 3}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PilotCard
